import React, { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Search, X } from "lucide-react";
import { useMenuHubApi } from "../../api/menuHub";
import { TransferListSkeleton } from "../skeletons";
import { useTheme } from "../../theme/useTheme";
import { AdminUser, ApiUserHospital, UserHospital, UserRequest } from "../../types";
import "./styles.scss";

interface UserFormModalProps {
    open: boolean;
    mode: "create" | "edit";
    user?: AdminUser | null;
    onClose: () => void;
    onSaved: () => void;
}

const NIVEIS_ACESSO = [
    { value: "FUNCIONARIO", label: "Funcionário" },
    { value: "COORDENADOR", label: "Coordenador" },
    { value: "GERENTE", label: "Gerente" },
    { value: "ADMINISTRADOR", label: "Administrador" },
] as const;

const HOSPITAIS: UserHospital[] = [
    { codigoHospital: "2", nomeHospital: "HSMC" },
    { codigoHospital: "33", nomeHospital: "HUC" },
];

const hospitalCodesFromUser = (hospitais?: ApiUserHospital[] | null) => {
    if (!Array.isArray(hospitais)) {
        return [] as string[];
    }

    const allowed = new Set(HOSPITAIS.map((hospital) => hospital.codigoHospital));

    return hospitais
        .map((hospital) => String(hospital?.cdHospital ?? hospital?.codigoHospital ?? ""))
        .filter((codigo) => allowed.has(codigo));
};

const UserFormModal: React.FC<UserFormModalProps> = ({
    open,
    mode,
    user,
    onClose,
    onSaved,
}) => {
    const api = useMenuHubApi();
    const apiRef = useRef(api);
    apiRef.current = api;
    const { isDarkMode } = useTheme();

    const [email, setEmail] = useState("");
    const [loginTasy, setLoginTasy] = useState("");
    const [nivelAcesso, setNivelAcesso] = useState("");
    const [hospitalCodes, setHospitalCodes] = useState<string[]>([]);
    const [available, setAvailable] = useState<string[]>([]);
    const [selected, setSelected] = useState<string[]>([]);
    const [availableSearch, setAvailableSearch] = useState("");
    const [selectedSearch, setSelectedSearch] = useState("");
    const [highlighted, setHighlighted] = useState<string[]>([]);
    const [loadingProfiles, setLoadingProfiles] = useState(false);
    const [loadingTasy, setLoadingTasy] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) {
            return;
        }

        let cancelled = false;
        setEmail(user?.email || "");
        setLoginTasy(user?.loginTasy || "");
        setNivelAcesso(user?.nivelAcesso || "");
        setHospitalCodes(hospitalCodesFromUser(user?.hospitais));
        setAvailableSearch("");
        setSelectedSearch("");
        setHighlighted([]);
        setError("");
        setLoadingProfiles(true);

        const loadProfiles = async () => {
            try {
                const allProfiles = await apiRef.current.getAllProfiles();
                const profiles = Array.isArray(allProfiles) ? allProfiles : [];

                if (mode === "edit" && user?.email) {
                    const response = await apiRef.current.getUserProfiles(user.email);
                    const userProfiles = Array.isArray(response?.perfis) ? response.perfis : [];

                    if (!cancelled) {
                        setSelected(userProfiles);
                        setAvailable(profiles.filter((profile: string) => !userProfiles.includes(profile)));
                        setNivelAcesso(response?.nivelAcesso || user.nivelAcesso || "");
                        setHospitalCodes(
                            hospitalCodesFromUser(response?.hospitais ?? user.hospitais),
                        );
                    }
                } else if (!cancelled) {
                    setSelected([]);
                    setAvailable(profiles);
                }
            } catch {
                if (!cancelled) {
                    setError("Não foi possível carregar as permissões.");
                    setSelected([]);
                    setAvailable([]);
                }
            } finally {
                if (!cancelled) {
                    setLoadingProfiles(false);
                }
            }
        };

        loadProfiles();

        return () => {
            cancelled = true;
        };
    }, [open, mode, user?.email, user?.loginTasy, user?.nivelAcesso, user?.hospitais]);

    useEffect(() => {
        if (!open) {
            return;
        }

        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape" && !saving) {
                onClose();
            }
        };

        document.addEventListener("keydown", closeOnEscape);
        return () => document.removeEventListener("keydown", closeOnEscape);
    }, [open, onClose, saving]);

    const filteredAvailable = useMemo(
        () => available.filter((item) => item.toLowerCase().includes(availableSearch.toLowerCase())),
        [available, availableSearch],
    );

    const filteredSelected = useMemo(
        () => selected.filter((item) => item.toLowerCase().includes(selectedSearch.toLowerCase())),
        [selected, selectedSearch],
    );

    const highlightedAvailable = highlighted.filter((item) => available.includes(item));
    const highlightedSelected = highlighted.filter((item) => selected.includes(item));

    if (!open) {
        return null;
    }

    const toggleHighlighted = (item: string) => {
        setHighlighted((current) =>
            current.includes(item)
                ? current.filter((highlightedItem) => highlightedItem !== item)
                : [...current, item],
        );
    };

    const moveToSelected = (items: string[]) => {
        if (!items.length) {
            return;
        }

        setAvailable((current) => current.filter((item) => !items.includes(item)));
        setSelected((current) => [...current, ...items.filter((item) => !current.includes(item))]);
        setHighlighted((current) => current.filter((item) => !items.includes(item)));
    };

    const moveToAvailable = (items: string[]) => {
        if (!items.length) {
            return;
        }

        setSelected((current) => current.filter((item) => !items.includes(item)));
        setAvailable((current) => [...current, ...items.filter((item) => !current.includes(item))]);
        setHighlighted((current) => current.filter((item) => !items.includes(item)));
    };

    const toggleHospital = (codigoHospital: string) => {
        setHospitalCodes((current) =>
            current.includes(codigoHospital)
                ? current.filter((codigo) => codigo !== codigoHospital)
                : [...current, codigoHospital],
        );
    };

    const fetchTasyUser = async () => {
        const normalizedEmail = email.trim();
        if (mode === "edit" || !normalizedEmail.includes("@")) {
            return;
        }

        setLoadingTasy(true);
        try {
            const response = await apiRef.current.getNmUsuarioByEmail(normalizedEmail);
            if (response?.nmUsuarios?.length) {
                setLoginTasy(response.nmUsuarios[0]);
            }
        } finally {
            setLoadingTasy(false);
        }
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        const normalizedEmail = email.trim();

        if (!normalizedEmail) {
            setError("Informe o e-mail do usuário.");
            return;
        }

        if (!nivelAcesso) {
            setError("Selecione o nível de acesso.");
            return;
        }

        const hospitais = HOSPITAIS.filter((hospital) => hospitalCodes.includes(hospital.codigoHospital));

        if (!hospitais.length) {
            setError("Selecione ao menos um hospital.");
            return;
        }

        setSaving(true);
        setError("");

        try {
            const payload: UserRequest = {
                email: normalizedEmail,
                loginTasy: loginTasy.trim(),
                solucoesPermitidas: selected.join(","),
                nivelAcesso,
                hospitais,
            };

            if (mode === "edit") {
                await apiRef.current.updateUser(payload);
            } else {
                await apiRef.current.createProfile(payload);
            }

            onSaved();
            onClose();
        } catch (requestError: unknown) {
            const apiError = requestError as {
                response?: { data?: { detalhe?: string; mensagem?: string } };
            };
            setError(
                apiError.response?.data?.detalhe ||
                apiError.response?.data?.mensagem ||
                "Não foi possível salvar o usuário.",
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="user-modal" role="presentation" onMouseDown={saving ? undefined : onClose}>
            <section
                className="user-modal__dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="user-modal-title"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <header className="user-modal__header">
                    <h2 id="user-modal-title">
                        {mode === "create" ? "Cadastrar Novo Usuário" : "Editar Usuário"}
                    </h2>
                    <button
                        type="button"
                        className="user-modal__close"
                        aria-label="Fechar"
                        onClick={onClose}
                        disabled={saving}
                    >
                        <X size={18} />
                    </button>
                </header>

                <form className="user-modal__form" onSubmit={handleSubmit}>
                    <div className="user-modal__body">
                        <div className="user-modal__fields">
                            <label>
                                <span>E-mail</span>
                                <input
                                    type="email"
                                    value={email}
                                    placeholder="email@exemplo.com"
                                    required
                                    disabled={mode === "edit"}
                                    onChange={(event) => setEmail(event.target.value)}
                                    onBlur={fetchTasyUser}
                                />
                            </label>
                            <label>
                                <span>Usuário Tasy</span>
                                <input
                                    type="text"
                                    value={loginTasy}
                                    placeholder={loadingTasy ? "Buscando usuário..." : "usuario.tasy"}
                                    onChange={(event) => setLoginTasy(event.target.value)}
                                />
                            </label>
                            <label>
                                <span>Nível de acesso</span>
                                <select
                                    value={nivelAcesso}
                                    required
                                    onChange={(event) => setNivelAcesso(event.target.value)}
                                >
                                    <option value="">Selecione</option>
                                    {NIVEIS_ACESSO.map((nivel) => (
                                        <option key={nivel.value} value={nivel.value}>
                                            {nivel.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <fieldset className="user-modal__hospitals">
                                <legend>Hospitais</legend>
                                <div className="user-modal__hospital-switches">
                                    {HOSPITAIS.map((hospital) => {
                                        const isOn = hospitalCodes.includes(hospital.codigoHospital);

                                        return (
                                            <div key={hospital.codigoHospital} className="user-modal__switch">
                                                <span>{hospital.nomeHospital}</span>
                                                <button
                                                    type="button"
                                                    role="switch"
                                                    aria-checked={isOn}
                                                    aria-label={hospital.nomeHospital}
                                                    className={isOn ? "is-on" : ""}
                                                    onClick={() => toggleHospital(hospital.codigoHospital)}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </fieldset>
                        </div>

                        <div className="user-modal__permissions">
                            <h3>Atribuir permissões</h3>

                            {loadingProfiles ? (
                                <TransferListSkeleton isDarkMode={isDarkMode} />
                            ) : (
                                <>
                                    <div className="user-modal__transfer">
                                        <div className="user-modal__column">
                                            <div className="user-modal__list-label">
                                                <span>Disponíveis</span>
                                                <span>{available.length}</span>
                                            </div>
                                            <div className="user-modal__search">
                                                <Search size={13} className="user-modal__search-icon" />
                                                <input
                                                    type="text"
                                                    placeholder="Procurar"
                                                    aria-label="Buscar permissões disponíveis"
                                                    value={availableSearch}
                                                    onChange={(event) => setAvailableSearch(event.target.value)}
                                                />
                                            </div>
                                            <div className="user-modal__list-card">
                                                {filteredAvailable.map((item) => (
                                                    <button
                                                        key={item}
                                                        type="button"
                                                        className={highlightedAvailable.includes(item) ? "is-selected" : ""}
                                                        aria-pressed={highlightedAvailable.includes(item)}
                                                        onClick={() => toggleHighlighted(item)}
                                                    >
                                                        {item}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="user-modal__transfer-actions">
                                            <button
                                                type="button"
                                                title="Mover todos"
                                                aria-label="Mover todas para selecionadas"
                                                onClick={() => moveToSelected(filteredAvailable)}
                                                disabled={!filteredAvailable.length}
                                            >
                                                »
                                            </button>
                                            <button
                                                type="button"
                                                title="Mover selecionados"
                                                aria-label="Mover marcadas para selecionadas"
                                                onClick={() => moveToSelected(highlightedAvailable)}
                                                disabled={!highlightedAvailable.length}
                                            >
                                                ›
                                            </button>
                                            <button
                                                type="button"
                                                title="Remover selecionados"
                                                aria-label="Mover marcadas para disponíveis"
                                                onClick={() => moveToAvailable(highlightedSelected)}
                                                disabled={!highlightedSelected.length}
                                            >
                                                ‹
                                            </button>
                                            <button
                                                type="button"
                                                title="Remover todos"
                                                aria-label="Mover todas para disponíveis"
                                                onClick={() => moveToAvailable([...selected])}
                                                disabled={!selected.length}
                                            >
                                                «
                                            </button>
                                        </div>

                                        <div className="user-modal__column">
                                            <div className="user-modal__list-label">
                                                <span>Selecionados</span>
                                                <span>{selected.length}</span>
                                            </div>
                                            <div className="user-modal__search">
                                                <Search size={13} className="user-modal__search-icon" />
                                                <input
                                                    type="text"
                                                    placeholder="Procurar"
                                                    aria-label="Buscar permissões selecionadas"
                                                    value={selectedSearch}
                                                    onChange={(event) => setSelectedSearch(event.target.value)}
                                                />
                                            </div>
                                            <div className="user-modal__list-card">
                                                {filteredSelected.map((item) => (
                                                    <button
                                                        key={item}
                                                        type="button"
                                                        className={highlightedSelected.includes(item) ? "is-selected" : ""}
                                                        aria-pressed={highlightedSelected.includes(item)}
                                                        onClick={() => toggleHighlighted(item)}
                                                    >
                                                        {item}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="user-modal__mobile-permissions">
                                        {selected.length > 0 && (
                                            <div className="user-modal__column">
                                                <div className="user-modal__list-label">
                                                    <span>Selecionados</span>
                                                    <span>{selected.length}</span>
                                                </div>
                                                <div className="user-modal__list-card user-modal__list-card--mobile">
                                                    {selected.map((item) => (
                                                        <div key={item} className="user-modal__mobile-item">
                                                            <span>{item}</span>
                                                            <button
                                                                type="button"
                                                                aria-label={`Remover ${item}`}
                                                                onClick={() => moveToAvailable([item])}
                                                            >
                                                                <X size={12} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <div className="user-modal__column">
                                            <div className="user-modal__list-label">
                                                <span>Disponíveis</span>
                                                <span>{available.length}</span>
                                            </div>
                                            <div className="user-modal__search">
                                                <Search size={13} className="user-modal__search-icon" />
                                                <input
                                                    type="text"
                                                    placeholder="Procurar"
                                                    aria-label="Buscar permissões disponíveis"
                                                    value={availableSearch}
                                                    onChange={(event) => setAvailableSearch(event.target.value)}
                                                />
                                            </div>
                                            <div className="user-modal__list-card user-modal__list-card--mobile">
                                                {filteredAvailable.map((item) => (
                                                    <div key={item} className="user-modal__mobile-item">
                                                        <span>{item}</span>
                                                        <button
                                                            type="button"
                                                            aria-label={`Adicionar ${item}`}
                                                            onClick={() => moveToSelected([item])}
                                                        >
                                                            <Plus size={12} />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {error && <p className="user-modal__error" role="alert">{error}</p>}

                        <footer className="user-modal__footer">
                            <button type="button" onClick={onClose} disabled={saving}>
                                Cancelar
                            </button>
                            <button type="submit" disabled={saving || loadingProfiles}>
                                {saving ? "Salvando..." : "Salvar"}
                            </button>
                        </footer>
                    </div>
                </form>
            </section>
        </div>
    );
};

export default UserFormModal;
